/**
 * Diagnose Project Customer References
 * This script examines how customer data is actually stored in projects
 */

import { directSupabase } from '../lib/supabaseClient';

export async function diagnoseProjectCustomers() {
  console.log('🔍 Starting project customer diagnosis...');
  
  try {
    // Step 1: Get all projects and their customer-related fields
    console.log('📊 Fetching all projects...');
    const { data: projects, error: projectError } = await directSupabase
      .from('projects')
      .select('id, project_name, customer_id, customer_name, customer_email')
      .limit(20); // Limit to first 20 for diagnosis
    
    if (projectError) {
      throw new Error(`Failed to fetch projects: ${projectError.message}`);
    }
    
    console.log(`Found ${projects?.length || 0} projects (showing first 20)`);
    
    if (!projects || projects.length === 0) {
      console.log('ℹ️ No projects found in database');
      return { success: true, message: 'No projects to analyze' };
    }
    
    // Step 2: Analyze customer_id patterns
    console.log('🔍 Analyzing customer_id patterns...');
    const customerIdAnalysis = {
      null: 0,
      uuid: 0,
      integer: 0,
      string: 0,
      other: 0,
      samples: [] as any[]
    };
    
    projects.forEach(project => {
      const sample = {
        project_name: project.project_name,
        customer_id: project.customer_id,
        customer_name: project.customer_name,
        customer_email: project.customer_email
      };
      
      if (project.customer_id === null) {
        customerIdAnalysis.null++;
      } else if (typeof project.customer_id === 'string') {
        if (project.customer_id.length === 36 && project.customer_id.includes('-')) {
          customerIdAnalysis.uuid++;
        } else if (/^\d+$/.test(project.customer_id)) {
          customerIdAnalysis.integer++;
        } else {
          customerIdAnalysis.string++;
        }
      } else if (typeof project.customer_id === 'number') {
        customerIdAnalysis.integer++;
      } else {
        customerIdAnalysis.other++;
      }
      
      customerIdAnalysis.samples.push(sample);
    });
    
    console.log('📊 Customer ID Analysis:');
    console.table(customerIdAnalysis);
    console.log('📋 Sample project data:');
    console.table(customerIdAnalysis.samples);
    
    // Step 3: Get sample migration mappings for comparison
    console.log('🔍 Getting sample migration mappings...');
    const { data: mappings, error: mappingError } = await directSupabase
      .from('customer_migration_mapping')
      .select('*')
      .limit(10);
    
    if (mappingError) {
      console.error('❌ Failed to fetch mappings:', mappingError);
    } else {
      console.log('📋 Sample migration mappings:');
      console.table(mappings);
    }
    
    // Step 4: Get sample customers for comparison
    console.log('🔍 Getting sample customers...');
    const { data: customers, error: customerError } = await directSupabase
      .from('customers')
      .select('id, name, email, phone_number')
      .limit(10);
    
    if (customerError) {
      console.error('❌ Failed to fetch customers:', customerError);
    } else {
      console.log('📋 Sample customers:');
      console.table(customers);
    }
    
    // Step 5: Try to find any potential matches manually
    console.log('🔍 Looking for potential matches...');
    const potentialMatches = [];
    
    if (mappings && customers) {
      for (const project of projects.slice(0, 5)) {
        if (project.customer_email) {
          const customerByEmail = customers.find(c => c.email === project.customer_email);
          if (customerByEmail) {
            potentialMatches.push({
              project: project.project_name,
              match_type: 'email',
              project_customer_id: project.customer_id,
              project_customer_email: project.customer_email,
              customer_id: customerByEmail.id,
              customer_name: customerByEmail.name
            });
          }
        }
        
        if (project.customer_name) {
          const customerByName = customers.find(c => 
            c.name.toLowerCase().includes(project.customer_name.toLowerCase()) ||
            project.customer_name.toLowerCase().includes(c.name.toLowerCase())
          );
          if (customerByName) {
            potentialMatches.push({
              project: project.project_name,
              match_type: 'name',
              project_customer_id: project.customer_id,
              project_customer_name: project.customer_name,
              customer_id: customerByName.id,
              customer_name: customerByName.name
            });
          }
        }
      }
    }
    
    if (potentialMatches.length > 0) {
      console.log('🎯 Found potential matches:');
      console.table(potentialMatches);
    } else {
      console.log('❌ No potential matches found between projects and migrated customers');
    }
    
    // Step 6: Recommendations
    console.log('💡 Recommendations:');
    if (customerIdAnalysis.null > 0) {
      console.log(`• ${customerIdAnalysis.null} projects have no customer_id - these may need manual linking`);
    }
    if (customerIdAnalysis.uuid > 0) {
      console.log(`• ${customerIdAnalysis.uuid} projects already have UUID customer_id - these may already be linked to JARVIS customers`);
    }
    if (customerIdAnalysis.integer > 0) {
      console.log(`• ${customerIdAnalysis.integer} projects have integer customer_id - these might be QuoteDock IDs that need mapping`);
    }
    if (customerIdAnalysis.string > 0) {
      console.log(`• ${customerIdAnalysis.string} projects have string customer_id - these need investigation`);
    }
    
    return {
      success: true,
      analysis: customerIdAnalysis,
      sampleProjects: customerIdAnalysis.samples,
      sampleMappings: mappings,
      sampleCustomers: customers,
      potentialMatches
    };
    
  } catch (error) {
    console.error('❌ Project diagnosis failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).diagnoseProjects = diagnoseProjectCustomers;
